/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Checkbox, FormControlLabel } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC } from 'react';
import { useStore } from '../../store';
import { ListType, ValueOf } from '../../types';

export type SelectAllProps = {
	type: ValueOf<typeof ListType>;
	isCheckboxDisabled: boolean;
	disableCheckbox: () => void;
};

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%',
		paddingLeft: 7,
		paddingRight: 23,
		justifyContent: (props: SelectAllProps): string =>
			props.type === ListType.CONTRACT ? 'space-between' : 'flex-start',
		color: theme.palette.info.dark,
		'& > .MuiFormControlLabel-label': {
			fontWeight: 'bold'
		}
	}
}));

const SelectAll: FC<SelectAllProps> = ({ type, disableCheckbox, isCheckboxDisabled }: SelectAllProps) => {
	const classes = useStyles({ type, disableCheckbox, isCheckboxDisabled });
	const { shouldUseAllContracts, toggleUseAllContracts } = useStore();

	const handleCheckboxClick = (): void => {
		disableCheckbox();
		toggleUseAllContracts();
	};

	return (
		<FormControlLabel
			className={classes.root}
			disabled={isCheckboxDisabled}
			labelPlacement={'start'}
			control={<Checkbox checked={shouldUseAllContracts} onClick={handleCheckboxClick} size={'small'} />}
			label="Alle selektieren"
		/>
	);
};

export default SelectAll;
